import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Box } from "@mui/material";
import { Bar, Line } from "recharts";
import codeUtil from "@plannel/pages/utils/code-util";
import numberUtil from "@plannel/utils/number-util";
import { FONT } from "@plannel/components/ai/typographyTokens";
import { DashboardComposedChart } from "@plannel/components/dashboard/DashboardChart";
import SeriesLegend from "@plannel/components/dashboard/SeriesLegend";
import AiSegmentedControl from "@plannel/pages/ai/AiSegmentedControl";
import {
  COST_FIELD, TOKEN_KEYS, TOKEN_LABEL_KEYS, UC, chart, fmtBase, fmtInt, num, seriesColor,
} from "./llmUsageUtil";

const { DAY, WEEK, MONTH } = codeUtil.buckets;

// 주는 ISO 주(월요일 시작), 월은 YYYY-MM — 기간 양 끝의 짧은 주·월은 그대로 둔다
const bucketKey = (dt, bucket) => {
  if (bucket === MONTH) return dt.slice(0, 7);
  if (bucket === WEEK) {
    const d = new Date(`${dt}T00:00:00Z`);
    d.setUTCDate(d.getUTCDate() - ((d.getUTCDay() + 6) % 7));
    return d.toISOString().slice(0, 10);
  }
  return dt;
};

const fmtAxisTok = (v) => numberUtil.formatNumber(v, { notation: "compact", maximumFractionDigits: 1 });
const fmtAxisCost = (v) => numberUtil.formatNumber(v, { maximumFractionDigits: 4 });

const LlmUsageTrendChart = ({ daily, currency, baseCurrency }) => {
  const { t } = useTranslation();
  const [bucket, setBucket] = useState(DAY);
  const [hidden, setHidden] = useState({});

  // 공용 차트 셸은 X축 tickFormatter 를 받지 않아 표시용 라벨을 데이터에 넣는다(월은 YYYY-MM 그대로)
  const data = useMemo(() => Object.values((daily || []).reduce((acc, d) => {
    const key = bucketKey(d.usage_dt, bucket);
    const row = acc[key] || (acc[key] = {
      usage_dt: key, label: bucket === MONTH ? key : key.slice(5),
      ...Object.fromEntries(TOKEN_KEYS.map((k) => [k, 0])), [COST_FIELD]: 0, priced: false, baseCost: 0,
    });
    TOKEN_KEYS.forEach((k) => { row[k] += num(d[k]); });
    row[COST_FIELD] += num((d.costs || []).find((c) => c.currency_cd === currency)?.cost_amt);
    // 버킷 안에 환산 못 한 날이 하루라도 있으면 버킷 환산액을 비운다(비용 없는 날은 영향 없음)
    if ((d.costs || []).length > 0) {
      row.priced = true;
      row.baseCost = row.baseCost == null || d.base_cost_amt == null ? null : row.baseCost + num(d.base_cost_amt);
    }
    return acc;
  }, {})).sort((x, y) => (x.usage_dt < y.usage_dt ? -1 : 1)), [daily, bucket, currency]);

  const showBase = !!baseCurrency && baseCurrency !== currency;
  const fmtCostTip = (v, row) => {
    const main = fmtBase(v, currency);
    return showBase && row?.priced && row.baseCost != null ? `${main} (${fmtBase(row.baseCost, baseCurrency)})` : main;
  };

  const costLabel = t("llmUsageEstCost");
  const series = [
    ...TOKEN_KEYS.map((k) => ({ key: k, label: t(TOKEN_LABEL_KEYS[k]) })),
    ...(currency ? [{ key: COST_FIELD, label: costLabel }] : []),
  ];
  // 전부 끄면 빈 차트가 되므로 마지막 1개는 남긴다
  const toggle = (key) => setHidden((h) => {
    const next = { ...h, [key]: !h[key] };
    return series.some((s) => !next[s.key]) ? next : h;
  });

  return (
    <>
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 1, mb: 1 }}>
        <Box component="h3" sx={{ m: 0, fontSize: FONT.TITLE, fontWeight: 600, color: UC.text1 }}>
          {t("llmUsageDailyTrend")}
        </Box>
        <AiSegmentedControl value={bucket} onChange={setBucket} sx={{ flexWrap: "nowrap", flexShrink: 0 }}
          options={[{ value: DAY, label: t("day") }, { value: WEEK, label: t("week") }, { value: MONTH, label: t("month") }]} />
      </Box>
      {/* 공용 차트 셸이 축 제목을 받지 않아 비용 축(오른쪽) 단위를 위에 따로 적는다 */}
      {currency && !hidden[COST_FIELD] && (
        <Box sx={{ textAlign: "right", pr: 1, fontSize: FONT.CAPTION, lineHeight: 1, color: chart.axisText }}>{currency}</Box>
      )}
      <DashboardComposedChart
        data={data}
        xKey="label"
        barCategoryGap="28%"
        yAxes={[
          { id: "tok", tickFormatter: fmtAxisTok },
          ...(currency ? [{ id: "cost", orientation: "right", tickFormatter: fmtAxisCost }] : []),
        ]}
        tooltipFormatter={(v, name, entry) => (name === costLabel ? fmtCostTip(v, entry?.payload) : fmtInt(v))}
      >
        {TOKEN_KEYS.map((k) => !hidden[k] && (
          <Bar key={k} yAxisId="tok" dataKey={k} name={t(TOKEN_LABEL_KEYS[k])} stackId="tok"
            fill={seriesColor(k)} isAnimationActive={false} maxBarSize={28} />
        ))}
        {currency && !hidden[COST_FIELD] && (
          <Line yAxisId="cost" dataKey={COST_FIELD} name={costLabel}
            type="monotone" stroke={seriesColor(COST_FIELD)} strokeWidth={2.5} isAnimationActive={false}
            dot={{ r: 4, fill: chart.pointFill, stroke: seriesColor(COST_FIELD), strokeWidth: 2 }}
            activeDot={{ r: 4, fill: chart.pointFill, stroke: seriesColor(COST_FIELD), strokeWidth: 2.5 }} />
        )}
      </DashboardComposedChart>
      <SeriesLegend items={series.map((x) => ({ key: x.key, label: x.label, color: seriesColor(x.key) }))}
        isActive={(key) => !hidden[key]} onToggle={toggle} gap={0.75} />
    </>
  );
};

export default LlmUsageTrendChart;
